/**
 * TagInput Component
 * Reusable component for adding and managing tags
 */

import React, { useState, KeyboardEvent } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  maxTags?: number;
}

const TagInput: React.FC<TagInputProps> = ({
  tags,
  onTagsChange,
  suggestions = [],
  placeholder = 'Add tags...',
  maxTags = 10
}) => {
  const { theme } = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim());
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag on backspace if input is empty
      removeTag(tags[tags.length - 1]);
    }
  };

  const addTag = (tag: string) => {
    const cleanTag = tag.toLowerCase().trim();
    
    if (!cleanTag) return;
    if (tags.length >= maxTags) {
      alert(`Maximum ${maxTags} tags allowed`);
      return;
    }
    if (tags.includes(cleanTag)) {
      alert('Tag already added');
      return;
    }

    onTagsChange([...tags, cleanTag]);
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const filteredSuggestions = suggestions.filter(
    suggestion => 
      !tags.includes(suggestion) && 
      suggestion.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        padding: '8px',
        background: theme.colors.bgSecondary,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: '6px',
        minHeight: '42px',
        alignItems: 'center',
        transition: 'all 0.3s ease'
      }}>
        {/* Existing Tags */}
        {tags.map(tag => (
          <div
            key={tag}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              background: theme.colors.brand,
              color: theme.colors.bgPrimary,
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: 500,
              transition: 'all 0.2s ease'
            }}
          >
            <span>{tag}</span>
            <button
              onClick={() => removeTag(tag)}
              style={{
                background: 'none',
                border: 'none',
                color: theme.colors.bgPrimary,
                cursor: 'pointer',
                padding: '0',
                fontSize: '16px',
                lineHeight: '1',
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              title="Remove tag"
            >
              ×
            </button>
          </div>
        ))}

        {/* Input */}
        {tags.length < maxTags && (
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(e.target.value.length > 0);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(inputValue.length > 0)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={tags.length === 0 ? placeholder : ''}
            style={{
              flex: 1,
              minWidth: '120px',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: theme.colors.textPrimary,
              fontSize: '14px',
              padding: '4px'
            }}
          />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          background: theme.colors.bgSecondary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '6px',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          {filteredSuggestions.slice(0, 10).map(suggestion => (
            <div
              key={suggestion}
              onClick={() => addTag(suggestion)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                color: theme.colors.textPrimary,
                fontSize: '14px',
                borderBottom: `1px solid ${theme.colors.border}`,
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.colors.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}

      {/* Helper Text */}
      <div style={{
        fontSize: '12px',
        color: theme.colors.textSecondary,
        marginTop: '6px'
      }}>
        Press Enter to add tags. {tags.length}/{maxTags} tags
      </div>
    </div>
  );
};

export default TagInput;
